import React, { useState, useEffect } from 'react'
import { useAppSelector, useAppDispatch } from '@/hooks/storeHooks/storehooks'
import { formatDefPayload, formatNumbers } from '@/lib/utils'
import {
  fetchOverViewSiteMetrics,
  fetchOverViewOrderMetrics,
  fetchOrderInProgressStatus,
  fetchCompletedOrders,
  fetchOrdersClosedPerWeek,
  fetchSitesCompletedPerWeek,
  fetchVolumeGrid,
} from '@/services/apis/api'
import {
  setOverviewSiteMetrics,
  setOverviewOrderMetrics,
  setIsSitesMetricsLoading,
  setIsOrderMetricsLoading,
} from '@/redux-store/slices/OverviewSlice/SitesOverViewSlice'
import {
  OverviewSiteMetricsProps,
  OverviewOrderMetricsProps,
  FetchOrderInProgressStatusResponse,
  Metrics,
  OverviewOrderProgressProps,
  GraphData,
  Week,
} from '@/services/types/api.types'

import { toast } from 'sonner'
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts'
import { DropDownCmp } from '@/components/atom/dropdown/dropDown'
import { RedirectNewPageMobileIcon } from '@/assets/svgIcons'
import { useNavigate } from 'react-router-dom'

interface MobileServiceDeliveryOverviewProps {
  onSitesClick?: () => void
  onOrdersClick?: () => void
}

interface TabData {
  id: string
  label: string
  count: number
}

interface MetricCardProps {
  title: string
  total: number
  completed: number
  inProgress: number
  pastDue?: number
  onViewAll?: () => void
}

interface DonutChartProps {
  data: Array<{ name: string; value: number; color: string }>
  total: number
  centerLabel: string
}

interface HeatmapGridProps {
  data: Array<{ label: string; value: number; color: string }>
}

interface LineChartProps {
  data: Array<{ date: string; orderCount: number }>
  title?: string
}

interface VolumeGridProps {
  data: Array<{
    week: string
    mon: number
    tue: number
    wed: number
    thu: number
    fri: number
    sat: number
    sun: number
  }>
  loading?: boolean
}

// API Response Types
interface CompletedOrdersResponse {
  status: number
  data: {
    data: {
      graphData: Week[]
    }
  }
}

interface OrdersClosedWeeklyResponse {
  status: number
  data: {
    data: {
      graph: Week[]
    }
  }
}

interface SitesCompletedResponse {
  status: number
  data: {
    data: {
      graph: Week[]
    }
  }
}

interface VolumeGridResponse {
  status: number
  data: {
    data: GraphData
  }
}

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  total,
  completed,
  inProgress,
}) => (
  <div
    className='bg-primary rounded-radius-md p-padding-xl border border-secondary-border shadow-[0_4px_4px_0_rgba(129,129,129,0.25)]
'
  >
    <div className='flex items-center justify-between mb-3'>
      <h3 className='text-sm font-semibold text-primary-foreground'>{title}</h3>
    </div>

    <div className='flex justify-between'>
      <div className=''>
        <div className='text-2xl font-bold text-info'>
          {formatNumbers(total)}
        </div>
        <div className='text-sm text-primary-foreground'>Total</div>
      </div>
      <div className=''>
        <div className='text-2xl font-bold text-success'>
          {formatNumbers(completed)}
        </div>
        <div className='text-sm text-primary-foreground'>Completed</div>
      </div>
      <div className=''>
        <div className='text-2xl font-bold text-warning'>
          {formatNumbers(inProgress)}
        </div>
        <div className='text-sm text-primary-foreground'>In Progress</div>
      </div>
    </div>
  </div>
)

// Legend Component for mobile view
const MobileLegend: React.FC<{
  data: Array<{ name: string; value: number; color: string }>
}> = ({ data }) => (
  <div className='flex flex-wrap gap-2 justify-center'>
    {data
      .filter(item => item.value > 0)
      .map((item, index) => (
        <div
          key={index}
          className='flex items-center gap-2 bg-primary rounded-full px-2'
        >
          <div
            className='w-4 h-4  rounded-radius-xs flex-shrink-0'
            style={{ backgroundColor: item.color }}
          />
          <span className='text-lg font-normal' style={{ color: item.color }}>
            {item.name} ({item.value})
          </span>
        </div>
      ))}
  </div>
)

const DonutChart: React.FC<DonutChartProps> = ({
  data,
  total,
  centerLabel,
}) => {
  const COLORS = data.map(item => item.color)

  return (
    <div className='w-full'>
      {/* Chart Container */}
      <div className='relative w-full h-[20rem] mb-4'>
        <ResponsiveContainer width='100%' height='100%'>
          <PieChart>
            <Pie
              data={data}
              cx='50%'
              cy='50%'
              innerRadius={90}
              outerRadius={140}
              paddingAngle={1}
              dataKey='value'
            >
              {data.map((_, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length]}
                />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>

        <div className='absolute inset-0 flex flex-col items-center justify-center'>
          <div className='text-5xl font-bold text-[#000000]'>{total}</div>
          <div className='text-[27px] text-[#000000]'>{centerLabel}</div>
        </div>
      </div>

      {/* Mobile Legend */}
      <MobileLegend data={data} />
    </div>
  )
}

const HeatmapGrid: React.FC<HeatmapGridProps> = ({ data }) => (
  <div className='w-full'>
    <div className='flex h-24 rounded-lg overflow-hidden gap-[2px]'>
      {data.map((item, index) => (
        <div
          key={index}
          className={`flex-1 flex items-center justify-center text-primary-foreground font-bold text-2xl ${item.color}`}
        >
          {item.value}
        </div>
      ))}
    </div>
    <div className='flex justify-between mt-2 text-xs  font-semibold text-primary-foreground'>
      {data.map((item, index) => (
        <span key={index} className='flex-1 text-center'>
          {item.label}
        </span>
      ))}
    </div>
  </div>
)

const SimpleLineChart: React.FC<LineChartProps> = ({ data }) => (
  <div className='w-full h-36 '>
    <ResponsiveContainer width='100%' height='100%'>
      <LineChart data={data}>
        <CartesianGrid strokeDasharray='3 3' stroke='#e0e0e0' />
        <XAxis
          dataKey='date'
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 10 }}
        />
        <YAxis hide />
        <Line
          type='monotone'
          dataKey='orderCount'
          stroke='#ef4444'
          strokeWidth={2}
          dot={false}
        />
      </LineChart>
    </ResponsiveContainer>
  </div>
)

const VolumeGrid: React.FC<VolumeGridProps> = ({ data, loading }) => {
  const getCellColor = (value: number, globalAverage: number) => {
    if (globalAverage === 0) return 'bg-transparent text-primary-foreground'

    const twentyFive = 0.25 * globalAverage
    const fifty = globalAverage
    const seventyFive = 1.5 * globalAverage
    const hundred = 2 * globalAverage

    if (value === 0) return 'bg-transparent text-primary-foreground'
    if (value <= twentyFive)
      return 'bg-success-dim-light text-primary-foreground'
    if (value <= fifty) return 'bg-info-dim-light text-primary-foreground'
    if (value <= seventyFive)
      return 'bg-warning-dim-light text-primary-foreground'
    if (value <= hundred) return 'bg-danger-dim-light text-primary-foreground'
    return 'bg-danger-dim-light text-primary-foreground'
  }

  // Calculate global average for color coding
  const allValues = data.flatMap(row => [
    row.mon,
    row.tue,
    row.wed,
    row.thu,
    row.fri,
    row.sat,
    row.sun,
  ])
  const globalAverage =
    allValues.length > 0
      ? allValues.reduce((sum, val) => sum + val, 0) / allValues.length
      : 0

  if (loading) {
    return (
      <div className='space-y-2'>
        <div className='grid grid-cols-8 gap-1 text-xs text-primary-foreground font-medium'>
          <span>Week</span>
          <span className='text-center'>Mon</span>
          <span className='text-center'>Tue</span>
          <span className='text-center'>Wed</span>
          <span className='text-center'>Thu</span>
          <span className='text-center'>Fri</span>
          <span className='text-center'>Sat</span>
          <span className='text-center'>Sun</span>
        </div>
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className='grid grid-cols-8 gap-1 items-center'>
            <div className='h-8 bg-gray-200 rounded animate-pulse'></div>
            {Array.from({ length: 7 }).map((_, dayIndex) => (
              <div
                key={dayIndex}
                className='h-8 bg-gray-200 rounded animate-pulse'
              ></div>
            ))}
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className='space-y-2'>
      <div className='grid grid-cols-8 gap-1 text-xs text-primary-foreground font-medium'>
        <span>Week</span>
        <span className='text-center'>Mon</span>
        <span className='text-center'>Tue</span>
        <span className='text-center'>Wed</span>
        <span className='text-center'>Thu</span>
        <span className='text-center'>Fri</span>
        <span className='text-center'>Sat</span>
        <span className='text-center'>Sun</span>
      </div>
      {data.length > 0 ? (
        data.map((row, index) => (
          <div key={index} className='grid grid-cols-8 gap-1 items-center'>
            <span className='text-sm font-medium text-primary-foreground'>
              {row.week}
            </span>
            <div
              className={`text-center py-2 rounded text-sm font-medium ${getCellColor(row.mon, globalAverage)}`}
            >
              {row.mon}
            </div>
            <div
              className={`text-center py-2 rounded text-sm font-medium ${getCellColor(row.tue, globalAverage)}`}
            >
              {row.tue}
            </div>
            <div
              className={`text-center py-2 rounded text-sm font-medium ${getCellColor(row.wed, globalAverage)}`}
            >
              {row.wed}
            </div>
            <div
              className={`text-center py-2 rounded text-sm font-medium ${getCellColor(row.thu, globalAverage)}`}
            >
              {row.thu}
            </div>
            <div
              className={`text-center py-2 rounded text-sm font-medium ${getCellColor(row.fri, globalAverage)}`}
            >
              {row.fri}
            </div>
            <div
              className={`text-center py-2 rounded text-sm font-medium ${getCellColor(row.sat, globalAverage)}`}
            >
              {row.sat}
            </div>
            <div
              className={`text-center py-2 rounded text-sm font-medium ${getCellColor(row.sun, globalAverage)}`}
            >
              {row.sun}
            </div>
          </div>
        ))
      ) : (
        <div className='text-center py-4 text-secondary-foreground'>
          No data available for the selected time range
        </div>
      )}
    </div>
  )
}

const TabNavigation: React.FC<{
  tabs: TabData[]
  activeTab: string
  onTabChange: (tabId: string) => void
}> = ({ tabs, activeTab, onTabChange }) => (
  <div className='border-b border-t border-disable mb-4'>
    <div className='flex overflow-x-auto scrollbar-hide'>
      {tabs.map(tab => (
        <button
          key={tab.id}
          onClick={() => onTabChange(tab.id)}
          className={`flex-shrink-0 min-w-[87.75px] py-[9px] px-padding-sm text-base font-medium border-b-2 transition-colors whitespace-nowrap ${
            activeTab === tab.id
              ? 'border-primary-active-foreground font-semibold text-primary-active-foreground'
              : 'border-transparent text-secondary-foreground font-normal hover:text-primary-foreground'
          }`}
        >
          {tab.label}
        </button>
      ))}
    </div>
  </div>
)

const MobileServiceDeliveryOverview: React.FC<
  MobileServiceDeliveryOverviewProps
> = ({ onSitesClick, onOrdersClick }) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const defPayload = useAppSelector(state => state.menu.payloadDef)
  const essentialIds = formatDefPayload(defPayload)

  const siteMetricsData = useAppSelector(
    state => state.sitesOverview.overViewSiteMetrics,
  ) as Metrics

  const orderMetricsData = useAppSelector(
    state => state.sitesOverview.overViewOrderMetrics,
  ) as Metrics

  const isSiteLoading = useAppSelector(
    state => state.sitesOverview.isSiteMetricsLoading,
  )
  const isOrderLoading = useAppSelector(
    state => state.sitesOverview.isOrderMetricsLoading,
  )

  const [activeTab, setActiveTab] = useState('summary')
  const [orderProgressData, setOrderProgressData] =
    useState<OverviewOrderProgressProps>({
      inProgress: 0,
      completed: 0,
      customerHold: 0,
      spectrotelHold: 0,
      total: 0,
    })
  const [completedOrdersData, setCompletedOrdersData] = useState<
    Array<{ date: string; orderCount: number }>
  >([])
  const [ordersClosedWeeklyData, setOrdersClosedWeeklyData] = useState<
    Array<{ date: string; orderCount: number }>
  >([])
  const [sitesCompletedData, setSitesCompletedData] = useState<
    Array<{ date: string; orderCount: number }>
  >([])
  const [volumeGridData, setVolumeGridData] = useState<
    Array<{
      week: string
      mon: number
      tue: number
      wed: number
      thu: number
      fri: number
      sat: number
      sun: number
    }>
  >([])
  const [isVolumeGridLoading, setIsVolumeGridLoading] = useState(false)

  // State for dropdown time ranges
  const [timeRanges, setTimeRanges] = useState({
    dueOrderVolumeGrid: 'LAST_MONTH',
    siteStatus: 'CURRENT_WEEK',
    sitePriorityStatus: 'CURRENT_WEEK',
    ordersInProgressStatus: 'CURRENT_WEEK',
    ordersCompletedOverTime: 'CURRENT_WEEK',
  })

  // Dropdown component
  const DropdownComponent = ({
    name,
    value,
    handleChange,
  }: {
    name: string
    value: string
    handleChange: (key: string, value: string) => void
  }) => {
    return (
      <div className='bg-secondary text-primary-foreground rounded-full w-auto'>
        <DropDownCmp
          node={{
            fieldName: name,
            mobile: true,
            dropdownValue:
              name === 'dueOrderVolumeGrid'
                ? [
                    { label: 'Last Month', value: 'LAST_MONTH' },
                    { label: 'Current Month', value: 'CURRENT_MONTH' },
                    { label: 'Next Month', value: 'NEXT_MONTH' },
                  ]
                : [
                    { label: '7 Days', value: 'LAST_7_DAYS' },
                    { label: 'Last Week', value: 'LAST_WEEK' },
                    { label: '30 Days', value: 'LAST_30_DAYS' },
                    { label: 'Last Month', value: 'LAST_MONTH' },
                    { label: 'Current Week', value: 'CURRENT_WEEK' },
                  ],
            value: value,
          }}
          handleChange={handleChange}
          className='bg-primary p-2 rounded-full text-xs'
        />
      </div>
    )
  }

  // Handle dropdown changes
  const handleDropdownChange = (key: string, value: string) => {
    setTimeRanges(prev => ({ ...prev, [key]: value }))

    // Refetch data based on the dropdown change
    switch (key) {
      case 'ordersCompletedOverTime':
        fetchCompletedOrdersData(value)
        break
      case 'siteStatus':
      case 'sitePriorityStatus':
        fetchSiteMetrics(value)
        break
      case 'ordersInProgressStatus':
        fetchOrderProgressStatus(value)
        break
      case 'dueOrderVolumeGrid':
        fetchVolumeGridData(value)
        break
    }
  }

  // Fetch data functions
  const fetchSiteMetrics = async (datetime?: string) => {
    dispatch(setIsSitesMetricsLoading(true))
    try {
      const res = (await fetchOverViewSiteMetrics({
        ...essentialIds,
        ...(datetime && { datetime }),
      })) as OverviewSiteMetricsProps
      if (res?.status === 200 && res?.data) {
        dispatch(setOverviewSiteMetrics(res.data.data.metrics))
      }
    } catch (error) {
      console.error('Failed to fetch site metrics:', error)
      toast.error('Failed to fetch site metrics')
    }
    dispatch(setIsSitesMetricsLoading(false))
  }

  const fetchOrderMetrics = async (datetime?: string) => {
    dispatch(setIsOrderMetricsLoading(true))
    try {
      const res = (await fetchOverViewOrderMetrics({
        ...essentialIds,
        ...(datetime && { datetime }),
      })) as OverviewOrderMetricsProps
      if (res?.status === 200 && res?.data) {
        dispatch(setOverviewOrderMetrics(res.data.data.metrics))
      }
    } catch (error) {
      console.error('Failed to fetch order metrics:', error)
      toast.error('Failed to fetch order metrics')
    }
    dispatch(setIsOrderMetricsLoading(false))
  }

  const fetchOrderProgressStatus = async (datetime?: string) => {
    try {
      const res = (await fetchOrderInProgressStatus({
        ...essentialIds,
        ...(datetime && { datetime }),
      })) as FetchOrderInProgressStatusResponse
      if (res?.status === 200 && res?.data) {
        setOrderProgressData(res.data.data.metrics)
      }
    } catch (error) {
      console.error('Failed to fetch order progress status:', error)
      toast.error('Failed to fetch order progress status')
    }
  }

  // For Orders tab - uses desktop API
  const fetchCompletedOrdersData = async (datetime?: string) => {
    try {
      const res = (await fetchCompletedOrders({
        ...essentialIds,
        ...(datetime && { datetime }),
      })) as CompletedOrdersResponse
      if (res?.status === 200 && res?.data) {
        const chartData = res.data.data.graphData.map((item: Week) => {
          const date = new Date(item.date)
          const formattedDate = date.toLocaleDateString('en-US', {
            month: 'numeric',
            day: 'numeric',
          })
          return {
            date: formattedDate,
            orderCount: item.orderCount,
          }
        })
        setCompletedOrdersData(chartData)
      }
    } catch (error) {
      console.error('Failed to fetch completed orders data:', error)
      toast.error('Failed to fetch completed orders data')
    }
  }

  // For Summary tab - uses weekly API
  const fetchOrdersClosedWeeklyData = async () => {
    try {
      const res = (await fetchOrdersClosedPerWeek({
        ...essentialIds,
      })) as OrdersClosedWeeklyResponse
      if (res?.status === 200 && res?.data) {
        const chartData = res.data.data.graph.reverse().map((item: Week) => {
          const date = new Date(item.date)
          const formattedDate = date.toLocaleDateString('en-US', {
            month: 'numeric',
            day: 'numeric',
          })
          return {
            date: formattedDate,
            orderCount: item.orderCount,
          }
        })
        setOrdersClosedWeeklyData(chartData)
      }
    } catch (error) {
      console.error('Failed to fetch orders closed weekly data:', error)
      toast.error('Failed to fetch orders closed weekly data')
    }
  }

  const fetchSitesCompletedData = async (datetime?: string) => {
    try {
      const res = (await fetchSitesCompletedPerWeek({
        ...essentialIds,
        ...(datetime && { datetime }),
      })) as SitesCompletedResponse
      if (res?.status === 200 && res?.data) {
        const chartData = res.data.data.graph.reverse().map((item: Week) => {
          const date = new Date(item.date)
          const formattedDate = date.toLocaleDateString('en-US', {
            month: 'numeric',
            day: 'numeric',
          })
          return {
            date: formattedDate,
            orderCount: item.orderCount,
          }
        })
        setSitesCompletedData(chartData)
      }
    } catch (error) {
      console.error('Failed to fetch sites completed data:', error)
      toast.error('Failed to fetch sites completed data')
    }
  }

  const fetchVolumeGridData = async (datetime?: string) => {
    setIsVolumeGridLoading(true)
    try {
      const res = (await fetchVolumeGrid({
        ...essentialIds,
        ...(datetime && { datetime }),
      })) as VolumeGridResponse
      if (res?.status === 200 && res?.data) {
        const graphData = res.data.data.graphData
        const transformedData = convertToVolumeGridData(graphData)
        setVolumeGridData(transformedData)
      }
    } catch (error) {
      console.error('Failed to fetch volume grid data:', error)
      toast.error('Failed to fetch volume grid data')
    }
    setIsVolumeGridLoading(false)
  }

  // Helper function to convert API data to mobile grid format
  const convertToVolumeGridData = (graphData: GraphData['graphData']) => {
    if (!graphData || typeof graphData !== 'object') {
      return []
    }

    const dayIndexMap = {
      Mon: 0,
      Tue: 1,
      Wed: 2,
      Thu: 3,
      Fri: 4,
      Sat: 5,
      Sun: 6,
    }

    const getDayIndex = (dateStr: string) => {
      const date = new Date(dateStr)
      if (isNaN(date.getTime())) return undefined
      const day = date.toLocaleDateString('en-US', {
        weekday: 'short',
      }) as keyof typeof dayIndexMap
      return dayIndexMap[day]
    }

    const weeks = Object.keys(graphData)

    return weeks.map(weekKey => {
      const dayArray = Array(7).fill(0) // [Mon, Tue, Wed, Thu, Fri, Sat, Sun]
      const weekData = graphData[weekKey]

      if (Array.isArray(weekData)) {
        weekData.forEach(entry => {
          const index = getDayIndex(entry.date)
          if (index !== undefined) {
            dayArray[index] = entry.orderCount
          }
        })
      }

      return {
        week: weekKey,
        mon: dayArray[0],
        tue: dayArray[1],
        wed: dayArray[2],
        thu: dayArray[3],
        fri: dayArray[4],
        sat: dayArray[5],
        sun: dayArray[6],
      }
    })
  }

  useEffect(() => {
    if (defPayload.customerId && defPayload.siteId.length > 0) {
      fetchSiteMetrics()
      fetchOrderMetrics()
      fetchOrderProgressStatus()
      fetchCompletedOrdersData(timeRanges.ordersCompletedOverTime)
      fetchOrdersClosedWeeklyData()
      fetchSitesCompletedData()
      fetchVolumeGridData(timeRanges.dueOrderVolumeGrid)
    }
  }, [defPayload])

  const tabs: TabData[] = [
    { id: 'summary', label: 'Summary', count: 0 },
    { id: 'sites', label: 'Sites', count: siteMetricsData?.total || 0 },
    { id: 'orders', label: 'Orders', count: orderMetricsData?.total || 0 },
  ]

  // Chart data preparation
  const siteDonutData = [
    {
      name: 'Completed',
      value: siteMetricsData?.completed || 0,
      color: 'var(--success)',
    },
    {
      name: 'In Progress',
      value: siteMetricsData?.inProgress || 0,
      color: 'var(--warning)',
    },
  ]

  const orderDonutData = [
    {
      name: 'Completed',
      value: orderProgressData.completed,
      color: 'var(--success)',
    },
    {
      name: 'In Progress',
      value: orderProgressData.inProgress,
      color: 'var(--warning)',
    },
    {
      name: 'Spectrotel Hold',
      value: orderProgressData.spectrotelHold,
      color: 'var(--info)',
    },
    {
      name: 'Customer Hold',
      value: orderProgressData.customerHold,
      color: 'var(--error)',
    },
  ]

  const siteHeatmapData = [
    {
      label: 'Completed',
      value: siteMetricsData?.completed || 0,
      color: 'bg-success',
    },
    {
      label: 'In Progress',
      value: siteMetricsData?.inProgress || 0,
      color: 'bg-warning',
    },
    {
      label: 'Past Due',
      value: siteMetricsData?.overdue || 0,
      color: 'bg-danger',
    },
  ]

  const renderTabContent = () => {
    switch (activeTab) {
      case 'summary':
        return (
          <div className='space-y-3'>
            {/* Orders Closed per week */}
            <div
              className='bg-primary rounded-radius-md p-padding-xl border border-secondary-border shadow-[0_4px_4px_0_rgba(129,129,129,0.25)]
'
            >
              <div className='flex items-center justify-between mb-3'>
                <h3 className='text-base font-semibold text-primary-foreground'>
                  Orders Closed per week
                </h3>
                <button
                  onClick={() => navigate('/service-delivery/orders')}
                  className='p-1'
                  aria-label='Go to orders overview'
                >
                  <RedirectNewPageMobileIcon />
                </button>
              </div>
              <SimpleLineChart data={ordersClosedWeeklyData} />
            </div>

            {/* Sites Completed per week */}
            <div
              className='bg-primary rounded-radius-md p-padding-xl border border-secondary-border shadow-[0_4px_4px_0_rgba(129,129,129,0.25)]
'
            >
              <div className='flex items-center justify-between mb-3'>
                <h3 className='text-base font-semibold text-primary-foreground'>
                  Sites Completed per week
                </h3>
                <button
                  onClick={() => navigate('/service-delivery/sites')}
                  className='p-1'
                  aria-label='Go to sites overview'
                >
                  <RedirectNewPageMobileIcon />
                </button>
              </div>
              <SimpleLineChart data={sitesCompletedData} />
            </div>

            {/* Due Order Volume Grid */}
            <div
              className='bg-primary rounded-radius-md p-padding-xl border border-secondary-border shadow-[0_4px_4px_0_rgba(129,129,129,0.25)]
'
            >
              <div className='flex items-center justify-between mb-3'>
                <h3 className='text-base font-semibold text-primary-foreground'>
                  Orders Due by Date
                </h3>
                <div className='text-sm'>
                  <DropdownComponent
                    name='dueOrderVolumeGrid'
                    value={timeRanges.dueOrderVolumeGrid}
                    handleChange={handleDropdownChange}
                  />
                </div>
              </div>
              <VolumeGrid data={volumeGridData} loading={isVolumeGridLoading} />
            </div>
          </div>
        )

      case 'sites':
        return (
          <div className='space-y-4'>
            {/* Site Status Donut Chart */}
            <div
              className='bg-primary rounded-radius-md p-padding-xl border border-secondary-border overflow-hidden shadow-[0_4px_4px_0_rgba(129,129,129,0.25)]
'
            >
              <div className='flex items-center justify-between mb-3'>
                <h3 className='text-base font-semibold text-primary-foreground'>
                  Site Status
                </h3>
              </div>
              <DonutChart
                data={siteDonutData}
                total={siteMetricsData?.total || 0}
                centerLabel='Total'
              />
            </div>

            {/* Site Priority & Status Metrics */}
            <div
              className='bg-primary rounded-radius-md p-padding-xl border border-secondary-border shadow-[0_4px_4px_0_rgba(129,129,129,0.25)]
'
            >
              <div className='flex items-center justify-between mb-3'>
                <h3 className='text-base font-semibold text-primary-foreground'>
                  Site priority & Status Metrics
                </h3>
                <div className='text-sm'>
                  <DropdownComponent
                    name='sitePriorityStatus'
                    value={timeRanges.sitePriorityStatus}
                    handleChange={handleDropdownChange}
                  />
                </div>
              </div>
              <HeatmapGrid data={siteHeatmapData} />
            </div>
          </div>
        )

      case 'orders':
        return (
          <div className='space-y-4'>
            {/* Orders in Progress Status */}
            <div
              className='bg-primary rounded-radius-md p-padding-xl border border-secondary-border overflow-hidden shadow-[0_4px_4px_0_rgba(129,129,129,0.25)]
'
            >
              <div className='flex items-center justify-between mb-3'>
                <h3 className='text-base font-semibold text-primary-foreground'>
                  Orders in Progress Status
                </h3>
              </div>
              <DonutChart
                data={orderDonutData}
                total={orderProgressData.total}
                centerLabel='Total'
              />
            </div>

            {/* Orders Completed Over Time */}
            <div
              className='bg-primary rounded-radius-md p-padding-xl border border-secondary-border shadow-[0_4px_4px_0_rgba(129,129,129,0.25)]
'
            >
              <div className='flex items-center justify-between mb-3'>
                <h3 className='text-base font-semibold text-primary-foreground'>
                  Orders Completed Over Time
                </h3>
                <div className='text-sm'>
                  <DropdownComponent
                    name='ordersCompletedOverTime'
                    value={timeRanges.ordersCompletedOverTime}
                    handleChange={handleDropdownChange}
                  />
                </div>
              </div>
              <SimpleLineChart
                data={completedOrdersData}
                title='Orders Completed'
              />
            </div>
          </div>
        )

      default:
        return (
          <div className='space-y-4'>
            {/* Sites Overview */}
            <MetricCard
              title='Sites'
              total={siteMetricsData?.total || 0}
              completed={siteMetricsData?.completed || 0}
              inProgress={siteMetricsData?.inProgress || 0}
              onViewAll={onSitesClick}
            />

            {/* Orders Overview */}
            <MetricCard
              title='Orders'
              total={orderMetricsData?.total || 0}
              completed={orderMetricsData?.completed || 0}
              inProgress={orderMetricsData?.inProgress || 0}
              onViewAll={onOrdersClick}
            />
          </div>
        )
    }
  }

  if (isSiteLoading || isOrderLoading) {
    return (
      <div className='flex flex-col gap-3 bg-primary p-3'>
        <div className='animate-pulse'>
          <div className='h-6 bg-gray-200 rounded w-48 mb-4'></div>
          <div className='grid grid-cols-2 gap-3 mb-4'>
            <div className='h-32 bg-gray-200 rounded-2xl'></div>
            <div className='h-32 bg-gray-200 rounded-2xl'></div>
          </div>
          <div className='h-8 bg-gray-200 rounded mb-4'></div>
          <div className='h-64 bg-gray-200 rounded-2xl'></div>
        </div>
      </div>
    )
  }

  return (
    <div className='flex flex-col '>
      {/* Note: Header is now handled by the parent container */}

      {/* Top Metric Cards - Fixed width with horizontal scroll */}
      <div className='overflow-x-auto scrollbar-hide'>
        <div className='flex flex-col gap-3 pb-2'>
          <div className='flex-shrink-0 w-full'>
            <MetricCard
              title='Sites'
              total={siteMetricsData?.total || 0}
              completed={siteMetricsData?.completed || 0}
              inProgress={siteMetricsData?.inProgress || 0}
              onViewAll={onSitesClick}
            />
          </div>
          <div className='flex-shrink-0 w-full'>
            <MetricCard
              title='Orders'
              total={orderMetricsData?.total || 0}
              completed={orderMetricsData?.completed || 0}
              inProgress={orderMetricsData?.inProgress || 0}
              onViewAll={onOrdersClick}
            />
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <TabNavigation
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {/* Tab Content */}
      {renderTabContent()}
    </div>
  )
}

export default MobileServiceDeliveryOverview
